import type { Recording, WorkflowStatus } from "./types";

export function recordingFilename(recording: Recording): string {
  return recording.filename || recording.original_filename || `Recording ${recording.id.slice(0, 8)}`;
}

export function recordingDuration(recording: Recording): number | undefined {
  const value = recording.duration_seconds ?? recording.duration;
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

export function recordingSize(recording: Recording): number | undefined {
  const value = recording.raw_size_bytes ?? recording.filesize;
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

export function recordingJobId(recording: Recording): string | undefined {
  return recording.hypnogram_job_id || recording.hypnogram_job || recording.job_id || undefined;
}

export function recordingStartTime(recording: Recording): Date | undefined {
  const raw = recording.recording_start_time || recording.start_time;
  if (!raw) return undefined;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export function normalizeStatus(status: WorkflowStatus | null | undefined): string {
  if (!status) return "pending";
  return String(status).trim().toLowerCase().replace(/[\s_]+/g, "-");
}

export function formatDuration(seconds: number | undefined): string {
  if (seconds === undefined || !Number.isFinite(seconds)) return "—";
  const whole = Math.max(0, Math.round(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = whole % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
    : `${minutes}:${String(secs).padStart(2, "0")}`;
}

export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || !Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
}

export function formatDate(value: string | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function isActiveStatus(status: WorkflowStatus | undefined): boolean {
  const normalized = normalizeStatus(status);
  return ["queued", "running", "processing", "uploading"].includes(normalized);
}
